import { useEffect, useState } from "react";
import { EMAIL_TEMPLATE_VARIABLES } from "@mizpah/shared";
import { api, AdminError } from "../adminClient.js";

interface TplRow { courseId: string; name: string; configured: boolean; updatedAt: string | null; }
interface Tpl { subject: string; bodyHtml: string; isDefault?: boolean; }

export default function EmailTemplatesPage() {
  const [rows, setRows] = useState<TplRow[]>([]);
  const [sel, setSel] = useState<string>("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [preview, setPreview] = useState<{ subject: string; html: string } | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [saved, setSaved] = useState(false);
  const [fee, setFee] = useState("");
  const [nextStart, setNextStart] = useState("");
  const [cfgSaved, setCfgSaved] = useState(false);

  useEffect(() => { api<{ items: TplRow[] }>("/admin/email-templates").then((d) => { setRows(d.items); if (d.items[0]) select(d.items[0].courseId); }).catch((e) => setError(msg(e))); }, []);

  async function select(courseId: string) {
    setSel(courseId); setPreview(null); setSaved(false); setCfgSaved(false); setError("");
    try {
      const d = await api<{ template: Tpl; config: { fee: string; nextStart: string } }>(`/admin/email-templates/${courseId}`);
      setSubject(d.template.subject); setBody(d.template.bodyHtml);
      setFee(d.config?.fee ?? ""); setNextStart(d.config?.nextStart ?? "");
    }
    catch (e) { setError(msg(e)); }
  }

  async function doSaveConfig() {
    setBusy("cfg"); setError(""); setCfgSaved(false);
    try { await api(`/admin/courses/${sel}`, { method: "PUT", body: { fee, nextStart } }); setCfgSaved(true); }
    catch (e) { setError(msg(e)); } finally { setBusy(""); }
  }

  const insertVar = (v: string) => setBody((b) => `${b}{{${v}}}`);

  async function doPreview() {
    setBusy("preview"); setError("");
    try { setPreview(await api<{ subject: string; html: string }>(`/admin/email-templates/${sel}/preview`, { method: "POST", body: { subject, bodyHtml: body } })); }
    catch (e) { setError(msg(e)); } finally { setBusy(""); }
  }
  async function doSave() {
    setBusy("save"); setError(""); setSaved(false);
    try { await api(`/admin/email-templates/${sel}`, { method: "PUT", body: { subject, bodyHtml: body } }); setSaved(true); const d = await api<{ items: TplRow[] }>("/admin/email-templates"); setRows(d.items); }
    catch (e) { setError(msg(e)); } finally { setBusy(""); }
  }

  return (
    <>
      <h1>Auto-email</h1>
      <p className="subhead">Edit the <strong>message</strong> each prospect receives, per course. The branded layout - header, program snapshot, brochure download button, signature and footer - is added automatically. Click a variable to insert it; it fills in when sent. Preview before saving.</p>
      {error && <div className="login-err" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="pills">
        {rows.map((r) => (
          <button key={r.courseId} className={`pill${sel === r.courseId ? " on" : ""}`} onClick={() => select(r.courseId)}>
            {r.name}{!r.configured && <span className="c">default</span>}
          </button>
        ))}
      </div>

      {sel && (
        <div className="card pad" style={{ marginBottom: 14 }}>
          <div className="alabel" style={{ marginTop: 0 }}>Program details (shown in the email's Next start and Tuition lines)</div>
          <div className="g2form" style={{ gridTemplateColumns: "1fr 1fr" }}>
            <label className="fld"><span className="alabel">Next start date</span>
              <input type="date" className="ainput" value={/^\d{4}-\d{2}-\d{2}$/.test(nextStart) ? nextStart : ""} onChange={(e) => setNextStart(e.target.value)} />
            </label>
            <label className="fld"><span className="alabel">Course fee</span>
              <input className="ainput" value={fee} onChange={(e) => setFee(e.target.value)} placeholder="e.g. $1,500" maxLength={60} />
            </label>
          </div>
          <div className="actions" style={{ marginTop: 12 }}>
            <button className="rbtn coral" disabled={busy === "cfg"} onClick={doSaveConfig}>{busy === "cfg" ? "Saving..." : "Save details"}</button>
            {cfgSaved && <span className="sub" style={{ color: "var(--ok)" }}>Saved ✓</span>}
          </div>
          <p className="sub" style={{ marginTop: 8, marginBottom: 0 }}>Leave Next start blank to show the prospect's selected month instead. These appear in the program snapshot, not the message body.</p>
        </div>
      )}

      {sel && (
        <div className="editor">
          <div className="card pad">
            <div className="alabel" style={{ marginTop: 0 }}>Variables</div>
            <div className="vars">{EMAIL_TEMPLATE_VARIABLES.map((v) => <button key={v} className="vchip" onClick={() => insertVar(v)}>{`{{${v}}}`}</button>)}</div>
            <div className="alabel">Subject</div>
            <input className="ainput" value={subject} onChange={(e) => setSubject(e.target.value)} />
            <div className="alabel">Message</div>
            <textarea className="ainput abody-input" value={body} onChange={(e) => setBody(e.target.value)} placeholder="The warm intro paragraph(s). The brochure button, program snapshot and signature are added for you." />
            <div className="actions" style={{ marginTop: 14 }}>
              <button className="rbtn" disabled={!!busy} onClick={doPreview}>{busy === "preview" ? "Rendering..." : "Preview"}</button>
              <button className="rbtn coral" disabled={!!busy} onClick={doSave}>{busy === "save" ? "Saving..." : "Save template"}</button>
              {saved && <span className="sub" style={{ color: "var(--ok)" }}>Saved ✓</span>}
            </div>
          </div>
          <div className="preview">
            <div className="alabel" style={{ marginTop: 0 }}>Preview (sample data)</div>
            {preview ? (
              <>
                <div className="em-h">{preview.subject}</div>
                <iframe className="em-frame" title="Email preview" sandbox="" srcDoc={preview.html} />
              </>
            ) : <p className="sub">Click Preview to see the rendered email.</p>}
          </div>
        </div>
      )}
    </>
  );
}

const msg = (e: unknown) => (e instanceof AdminError ? e.message : "Something went wrong");
